/**
 * Product add-ons module.
 * Toggling an add-on updates the running total and queues that variant to be
 * added to the cart alongside the main product on Add to Cart.
 */
class ProductAddonsModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.groups = controller.queryAll('[data-addons-group]');
    this.items = controller.queryAll('[data-addon-item]');
    this.quantityInput = controller.query('[data-quantity-input]');
    this.variantSelect = controller.query('[data-variant-select]');

    if (!this.items.length) return;

    this.selected = new Set();
    this.basePrice = this.readBasePrice();
    this.summary = this.buildSummary();

    this.section.productAddons = this;

    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.items.forEach((item) => {
      const toggle = item.querySelector('[data-addon-toggle]');
      toggle?.addEventListener('click', () => this.toggleItem(item));
    });

    this.section.addEventListener('variant:change', (event) => {
      this.basePrice = this.readBasePrice(event.detail?.variantOption);
      this.render();
    });

    this.quantityInput?.addEventListener('input', () => this.render());
    this.quantityInput?.addEventListener('change', () => this.render());

    document.addEventListener('cart:added', () => this.clearSelection());
  }

  readBasePrice(variantOption) {
    const option = variantOption || this.variantSelect?.selectedOptions?.[0];
    const fromOption = Number.parseInt(option?.dataset.variantPrice, 10);

    if (!Number.isNaN(fromOption)) return fromOption;

    if (typeof this.basePrice === 'number' && !Number.isNaN(this.basePrice)) {
      return this.basePrice;
    }

    const fromMarkup = Number.parseInt(this.groups[0]?.dataset.basePrice, 10);
    return Number.isNaN(fromMarkup) ? 0 : fromMarkup;
  }

  getQuantity() {
    const value = Number.parseInt(this.quantityInput?.value, 10);
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  toggleItem(item) {
    const variantId = item.dataset.addonVariantId;
    if (!variantId) return;

    const toggle = item.querySelector('[data-addon-toggle]');
    const isSelected = this.selected.has(variantId);

    if (isSelected) {
      this.selected.delete(variantId);
    } else {
      this.selected.add(variantId);
    }

    item.classList.toggle('product-addons__item--selected', !isSelected);
    toggle?.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
    toggle?.setAttribute(
      'aria-label',
      `${isSelected ? 'Add' : 'Remove'} ${item.dataset.addonTitle || 'add-on'}`
    );

    this.render();
  }

  clearSelection() {
    if (!this.selected.size) return;

    this.selected.clear();
    this.items.forEach((item) => {
      const toggle = item.querySelector('[data-addon-toggle]');
      item.classList.remove('product-addons__item--selected');
      toggle?.setAttribute('aria-pressed', 'false');
      toggle?.setAttribute('aria-label', `Add ${item.dataset.addonTitle || 'add-on'}`);
    });

    this.render();
  }

  getAddonsTotal() {
    return this.items.reduce((total, item) => {
      if (!this.selected.has(item.dataset.addonVariantId)) return total;
      return total + (Number.parseInt(item.dataset.addonPrice, 10) || 0);
    }, 0);
  }

  /** Consumed by the quantity-cart module when building the cart request. */
  getSelectedItems() {
    return this.items
      .filter((item) => this.selected.has(item.dataset.addonVariantId))
      .map((item) => ({ id: item.dataset.addonVariantId, quantity: 1 }));
  }

  buildSummary() {
    const lastGroup = this.groups[this.groups.length - 1];
    if (!lastGroup) return null;

    const summary = document.createElement('div');
    summary.className = 'product-addons-summary';
    summary.hidden = true;
    summary.innerHTML = `
      <div class="product-addons-summary__row">
        <span class="product-addons-summary__label" data-addons-count></span>
        <span class="product-addons-summary__value" data-addons-extra></span>
      </div>
      <div class="product-addons-summary__row product-addons-summary__row--total">
        <span class="product-addons-summary__label">Total</span>
        <span class="product-addons-summary__value" data-addons-total></span>
      </div>
    `;

    lastGroup.insertAdjacentElement('afterend', summary);
    return summary;
  }

  formatMoney(cents) {
    return window.Money?.format ? window.Money.format(cents) : `${(cents / 100).toFixed(2)}`;
  }

  render() {
    if (!this.summary) return;

    const count = this.selected.size;
    const addonsTotal = this.getAddonsTotal();

    this.summary.hidden = count === 0;
    if (!count) return;

    const total = this.basePrice * this.getQuantity() + addonsTotal;

    this.summary.querySelector('[data-addons-count]').textContent =
      `${count} add-on${count === 1 ? '' : 's'}`;
    this.summary.querySelector('[data-addons-extra]').textContent = `+${this.formatMoney(addonsTotal)}`;
    this.summary.querySelector('[data-addons-total]').textContent = this.formatMoney(total);
  }
}

window.HerbiProductSection.registerModule('addons', (controller) => {
  return new ProductAddonsModule(controller);
});
